import { Box, Typography } from "@mui/material";
import React from "react";
import Chart from "react-google-charts";
import {
    GoogleChartWrapper,
    GoogleViz,
    GoogleVizEventName,
} from "react-google-charts/dist/types";
import theme from "Theme/AppTheme";
import type { ZoomPathEntry } from "components/Signals/BaseObjects/Drilldown/DrilldownTypes";

export interface TreeMapSelectedNode {
    /** The clicked node's id (column 0 of the data row). */
    id: string;
    /** The parent node id, or null for the synthetic root. */
    parent: string | null;
    /** Cell size (column 2). */
    value: number;
    /** Data-table color column value. The renderer overrides fill color visually. */
    colorValue: number;
    /** True iff the row reports hasChildren in its metadata. */
    hasChildren: boolean;
}

export interface AcxTreeMapProps {
    /**
     * Google TreeMap data table shaped by `signalDataToTreeMapFormat`. Header
     * row followed by a synthetic root row (parent = null) followed by the
     * cells of the current level. Always one level's worth — per-level fetching
     * means the chart never sees a real multi-level tree. Column 0 cells may
     * be either a plain id string or Google's `{ v, f }` form (used when the
     * displayed label should differ from the click-through identifier).
     */
    data: (
        | string
        | number
        | null
        | { v: string; f: string }
    )[][];
    /**
     * Zoom path entries the chart is currently rendering at. Used to label the
     * deepest entry as the synthetic root's display name and to advise Google
     * which row to re-root against (the deepest entry's xIdentifier).
     */
    zoomPath: ZoomPathEntry[];
    /**
     * Set of cell ids whose children exist on the backend but aren't present in
     * `data`. Required because data only contains the current level — the chart
     * can't decide hasChildren by scanning its own rows. Builders compute this
     * from `rowHasChildren(row.metadata)`.
     */
    nodesWithChildren: Set<string>;
    /**
     * Single base color for the current depth. Higher-level callers pick
     * `levelBaseColor` per depth (typically from `vizOptions.colors` mod the
     * level index, falling back to a default palette). The TreeMap derives
     * lighter and darker shades from this color based on each cell's volume.
     */
    levelBaseColor: string;
    /**
     * Header text rendered above the chart canvas. Caller resolves `${N}`
     * placeholders against `zoomPath` (and falls back to `levelLabels[depth]`
     * or the deepest entry's display name) — the primitive just paints it.
     */
    headerLabel?: string;
    /** Fires when the user clicks a cell. */
    onSelectNode?: (node: TreeMapSelectedNode) => void;
    onChartReady?: (wrapper: GoogleChartWrapper, google: GoogleViz) => void;
    width?: number | string;
    height?: number | string;
}

const TREE_MAP_LIGHTEN_PERCENT = 18;
const TREE_MAP_DARKEN_PERCENT = 18;
// Visual constants applied via a one-shot SVG post-pass. Google TreeMap doesn't
// support rounded corners, inter-cell gaps, or multi-line cell labels natively,
// so we touch the rendered SVG once on `ready`. The fragility risk is
// acknowledged in the design doc — these are the visuals that turn the chart
// from "blocky" into a polished card.
const TREE_MAP_CORNER_RADIUS = 8;
const TREE_MAP_CELL_INSET = 2;
// In em units so they scale with the font-size we set in chart options.
const TREE_MAP_NAME_DY = "-0.4em";
const TREE_MAP_PERCENT_DY = "1.333em";
// Horizontal padding inside a cell — the name tspan won't extend past
// (rectWidth - 2 * this) before getting truncated with an ellipsis.
const TREE_MAP_LABEL_PADDING_PX = 8;
const TREE_MAP_ELLIPSIS = "…";

/**
 * Returns `{ minColor, maxColor }` derived from a single base color by shifting
 * lightness in HSL space. The base color sits at the level's mid point; min is
 * `+18%` lighter, max is `-18%` darker. Higher-y_percent cells (which Google
 * paints toward maxColor) read as the darker, more saturated shade.
 */
function deriveShadeRange(baseColorHex: string): {
    minColor: string;
    midColor: string;
    maxColor: string;
} {
    const hsl = hexToHsl(baseColorHex);
    if (!hsl) {
        return {
            minColor: baseColorHex,
            midColor: baseColorHex,
            maxColor: baseColorHex,
        };
    }
    const lighten = Math.min(95, hsl.l + TREE_MAP_LIGHTEN_PERCENT);
    const darken = Math.max(0, hsl.l - TREE_MAP_DARKEN_PERCENT);
    return {
        minColor: hslToHex({ h: hsl.h, s: hsl.s, l: lighten }),
        midColor: baseColorHex,
        maxColor: hslToHex({ h: hsl.h, s: hsl.s, l: darken }),
    };
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function deriveVolumeShade(
    baseColorHex: string,
    normalizedVolume: number,
): string {
    const hsl = hexToHsl(baseColorHex);
    if (!hsl) return baseColorHex;

    const volume = clamp(normalizedVolume, 0, 1);
    const lightness =
        hsl.l +
        TREE_MAP_LIGHTEN_PERCENT -
        (TREE_MAP_LIGHTEN_PERCENT + TREE_MAP_DARKEN_PERCENT) * volume;

    return hslToHex({
        h: hsl.h,
        s: hsl.s,
        l: clamp(lightness, 0, 100),
    });
}

function hexToHsl(
    hex: string,
): { h: number; s: number; l: number } | null {
    const normalized = hex.replace("#", "");
    if (normalized.length !== 6 && normalized.length !== 3) return null;
    const expand = (h: string) =>
        h.length === 3
            ? h
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : h;
    const expanded = expand(normalized);
    const r = parseInt(expanded.slice(0, 2), 16) / 255;
    const g = parseInt(expanded.slice(2, 4), 16) / 255;
    const b = parseInt(expanded.slice(4, 6), 16) / 255;
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let s = 0;
    let h = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h *= 60;
    }
    return { h, s: s * 100, l: l * 100 };
}

function hslToHex({
    h,
    s,
    l,
}: {
    h: number;
    s: number;
    l: number;
}): string {
    const sNorm = s / 100;
    const lNorm = l / 100;
    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const hPrime = (h % 360) / 60;
    const x = c * (1 - Math.abs((hPrime % 2) - 1));
    let [r, g, b] = [0, 0, 0];
    if (hPrime >= 0 && hPrime < 1) [r, g, b] = [c, x, 0];
    else if (hPrime < 2) [r, g, b] = [x, c, 0];
    else if (hPrime < 3) [r, g, b] = [0, c, x];
    else if (hPrime < 4) [r, g, b] = [0, x, c];
    else if (hPrime < 5) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    const m = lNorm - c / 2;
    const toHex = (n: number) => {
        const v = Math.round((n + m) * 255);
        return v.toString(16).padStart(2, "0");
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

type TreeMapRenderedRow = {
    row: number;
    id: string;
    label: string;
    percent: number;
};

function getDirectChildElement<T extends Element>(
    parent: Element | null,
    tagName: string,
): T | null {
    if (!parent) return null;
    const normalizedTag = tagName.toLowerCase();
    return (
        Array.from(parent.children).find(
            (child) => child.tagName.toLowerCase() === normalizedTag,
        ) as T | undefined
    ) ?? null;
}

function normalizeTreeMapLabel(value: unknown): string {
    return String(value ?? "").trim();
}

function getTextDisplayLabel(textEl: SVGTextElement): string {
    const existingTspans = textEl.querySelectorAll("tspan");
    if (existingTspans.length > 0) {
        return normalizeTreeMapLabel(existingTspans[0].textContent);
    }

    return normalizeTreeMapLabel(textEl.textContent);
}

function formatTreeMapPercent(value: number | undefined): string {
    const safeValue =
        typeof value === "number" && Number.isFinite(value) ? value : 0;
    const percent = Math.abs(safeValue) <= 1 ? safeValue * 100 : safeValue;
    if (percent > 0 && percent < 1) return "<1%";
    return `${Math.round(percent)}%`;
}

function getTreeMapRenderedRows(
    dataTable: {
        getNumberOfRows(): number;
        getFormattedValue(row: number, col: number): string;
        getValue(row: number, col: number): unknown;
    },
): TreeMapRenderedRow[] {
    const renderedRows: TreeMapRenderedRow[] = [];
    const rows = dataTable.getNumberOfRows();

    for (let i = 0; i < rows; i++) {
        const parent = dataTable.getValue(i, 1);
        if (parent == null || parent === "") continue;

        const id = String(dataTable.getValue(i, 0) ?? "");
        const label = normalizeTreeMapLabel(
            dataTable.getFormattedValue(i, 0) ?? id,
        );
        const percentValue = dataTable.getValue(i, 3);
        const numericPercent =
            typeof percentValue === "number" && Number.isFinite(percentValue)
                ? percentValue
                : 0;
        const normalizedPercent =
            Math.abs(numericPercent) <= 1
                ? numericPercent
                : numericPercent / 100;
        renderedRows.push({
            row: i,
            id,
            label,
            percent: clamp(normalizedPercent, 0, 1),
        });
    }

    return renderedRows;
}

function resolveRowFromLabel(
    label: string,
    rowsByLabel: Map<string, TreeMapRenderedRow[]>,
    claimedRows: Set<number>,
): TreeMapRenderedRow | undefined {
    const candidates = rowsByLabel.get(label);
    const exact = candidates?.find((row) => !claimedRows.has(row.row));
    if (exact) return exact;

    if (!label.endsWith(TREE_MAP_ELLIPSIS)) return undefined;

    const stem = label.slice(0, -1);
    for (const rows of rowsByLabel.values()) {
        const match = rows.find(
            (row) =>
                !claimedRows.has(row.row) && row.label.startsWith(stem),
        );
        if (match) return match;
    }

    return undefined;
}

function annotateCellGroupsWithRows(
    container: HTMLElement,
    renderedRows: TreeMapRenderedRow[],
): void {
    const rowsByLabel = new Map<string, TreeMapRenderedRow[]>();
    renderedRows.forEach((row) => {
        const existing = rowsByLabel.get(row.label) ?? [];
        existing.push(row);
        rowsByLabel.set(row.label, existing);
    });

    const cellGroups = Array.from(container.querySelectorAll("g"))
        .map((group) => {
            const rect = getDirectChildElement<SVGRectElement>(group, "rect");
            if (!rect) return undefined;
            const width = Number(rect.getAttribute("width") ?? 0);
            const height = Number(rect.getAttribute("height") ?? 0);
            if (width <= 0 || height <= 0) return undefined;
            const textEl = getDirectChildElement<SVGTextElement>(
                group,
                "text",
            );
            return {
                group,
                label: textEl ? getTextDisplayLabel(textEl) : "",
                area: width * height,
            };
        })
        .filter(
            (
                item,
            ): item is {
                group: SVGGElement;
                label: string;
                area: number;
            } => Boolean(item),
        );

    const claimedRows = new Set<number>();
    const nextUnclaimedRow = () =>
        renderedRows.find((row) => !claimedRows.has(row.row));

    const applyRowToGroup = (
        group: SVGGElement,
        resolved: TreeMapRenderedRow | undefined,
    ) => {
        if (!resolved) {
            group.removeAttribute("data-row");
            return;
        }

        claimedRows.add(resolved.row);
        if (group.getAttribute("data-row") !== String(resolved.row)) {
            group.setAttribute("data-row", String(resolved.row));
        }
    };

    cellGroups
        .filter((item) => item.label)
        .forEach((item) => {
            applyRowToGroup(
                item.group,
                resolveRowFromLabel(item.label, rowsByLabel, claimedRows),
            );
        });

    let unlabeledGroups = cellGroups.filter((item) => !item.label);
    const remainingRowsCount = renderedRows.length - claimedRows.size;
    if (unlabeledGroups.length > remainingRowsCount) {
        const maxArea = Math.max(...unlabeledGroups.map((item) => item.area));
        unlabeledGroups = unlabeledGroups.filter((item) => item.area < maxArea);
    }

    unlabeledGroups.forEach((item) => {
        const resolved = nextUnclaimedRow();
        if (!resolved) {
            item.group.removeAttribute("data-row");
            return;
        }

        applyRowToGroup(item.group, resolved);
    });
}

/**
 * Post-render styling pass: gives every rendered cell rounded corners + an
 * inter-cell gap, and replaces each cell's flat name label with a two-line
 * "{bold name} / {percent}" layout sourced from the data table's color column
 * (y_percent). Idempotent: marks each rect / text via data-attributes so
 * subsequent ready events don't double-apply the inset or re-layout the tspans.
 */
function applyTreeMapStyling(
    chartWrapper: GoogleChartWrapper,
    fontColor: string,
    levelBaseColor: string,
) {
    const container = document.getElementById(chartWrapper.getContainerId());
    if (!container) return;

    // Stretch the SVG to fill its parent. Google reserves ~10px on the right;
    // switching to a viewBox + non-uniform aspect ratio eliminates that gap.
    const svg = container.querySelector("svg");
    if (svg && !svg.getAttribute("viewBox")) {
        const w = svg.getAttribute("width");
        const h = svg.getAttribute("height");
        if (w && h) {
            svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
            svg.setAttribute("preserveAspectRatio", "none");
            svg.setAttribute("width", "100%");
            svg.setAttribute("height", "100%");
        }
    }

    const cornerRadiusStr = String(TREE_MAP_CORNER_RADIUS);
    container.querySelectorAll("rect").forEach((rect) => {
        if (rect.getAttribute("rx") !== cornerRadiusStr) {
            rect.setAttribute("rx", cornerRadiusStr);
            rect.setAttribute("ry", cornerRadiusStr);
        }
        if (rect.getAttribute("data-inset")) return;
        const x = Number(rect.getAttribute("x") ?? 0);
        const y = Number(rect.getAttribute("y") ?? 0);
        const w = Number(rect.getAttribute("width") ?? 0);
        const h = Number(rect.getAttribute("height") ?? 0);
        if (w <= TREE_MAP_CELL_INSET * 2 || h <= TREE_MAP_CELL_INSET * 2) {
            return;
        }
        rect.setAttribute("x", String(x + TREE_MAP_CELL_INSET));
        rect.setAttribute("y", String(y + TREE_MAP_CELL_INSET));
        rect.setAttribute("width", String(w - TREE_MAP_CELL_INSET * 2));
        rect.setAttribute("height", String(h - TREE_MAP_CELL_INSET * 2));
        rect.setAttribute("data-inset", "true");
    });

    const dataTable = chartWrapper.getDataTable();
    if (!dataTable) return;

    const typedDataTable = dataTable as unknown as {
        getNumberOfRows(): number;
        getFormattedValue(row: number, col: number): string;
        getValue(row: number, col: number): unknown;
    };
    const renderedRows = getTreeMapRenderedRows(typedDataTable);
    const rowInfoByRow = new Map(
        renderedRows.map((row) => [row.row, row] as const),
    );
    const maxVolumePercent = renderedRows.reduce(
        (max, row) => Math.max(max, row.percent),
        0,
    );
    annotateCellGroupsWithRows(container, renderedRows);

    container.querySelectorAll("g[data-row]").forEach((group) => {
        const row = Number(group.getAttribute("data-row"));
        const rowInfo = Number.isFinite(row) ? rowInfoByRow.get(row) : undefined;
        if (!rowInfo) return;

        const rect = getDirectChildElement<SVGRectElement>(group, "rect");
        if (!rect) return;

        const normalizedVolume =
            maxVolumePercent > 0
                ? rowInfo.percent / maxVolumePercent
                : 0;
        const fill = deriveVolumeShade(
            levelBaseColor,
            normalizedVolume,
        );
        if (rect.getAttribute("fill") !== fill) {
            rect.setAttribute("fill", fill);
        }
    });

    // Snapshot rect geometries up front so we can match each text element to
    // its containing rect by point-in-rect. The post-inset coordinates are
    // what's on the DOM at this point.
    const cellRects = Array.from(container.querySelectorAll("rect")).map(
        (rect) => ({
            x: Number(rect.getAttribute("x") ?? 0),
            y: Number(rect.getAttribute("y") ?? 0),
            w: Number(rect.getAttribute("width") ?? 0),
            h: Number(rect.getAttribute("height") ?? 0),
        }),
    );

    const findCellWidthForText = (textEl: SVGTextElement): number => {
        const textX = Number(textEl.getAttribute("x") ?? 0);
        const textY = Number(textEl.getAttribute("y") ?? 0);
        for (const r of cellRects) {
            if (
                textX >= r.x &&
                textX <= r.x + r.w &&
                textY >= r.y &&
                textY <= r.y + r.h
            ) {
                return r.w;
            }
        }
        return Number.POSITIVE_INFINITY;
    };

    const svgNs = "http://www.w3.org/2000/svg";
    container.querySelectorAll("text").forEach((rawText) => {
        const textEl = rawText as SVGTextElement;
        const existingTspans = textEl.querySelectorAll("tspan");
        const parentG = textEl.closest("g");
        const row = Number(parentG?.getAttribute("data-row"));
        const rowInfo = Number.isFinite(row) ? rowInfoByRow.get(row) : undefined;
        if (!rowInfo) return;
        const label = rowInfo.label;
        const expectedPct = formatTreeMapPercent(rowInfo.percent);

        // True idempotency check: if the element already has exactly two
        // tspans, the first matches `label` (or a truncated form) AND the
        // second matches the expected percent string, skip. This is what
        // lets us run the styling pass freely from a MutationObserver
        // without ping-ponging — same input produces no DOM writes, so the
        // observer doesn't re-fire.
        if (existingTspans.length === 2) {
            const firstSpanText = existingTspans[0].textContent ?? "";
            const secondSpanText = existingTspans[1].textContent ?? "";
            const firstMatches =
                firstSpanText === label ||
                (firstSpanText.endsWith(TREE_MAP_ELLIPSIS) &&
                    label.startsWith(firstSpanText.slice(0, -1)));
            if (firstMatches && secondSpanText === expectedPct) {
                return;
            }
        }

        const x = textEl.getAttribute("x") ?? "0";

        // Available width for the name = the containing rect's width minus
        // padding on both sides. If we can't find a rect (defensive), the
        // available width is infinity and no truncation happens.
        const cellWidth = findCellWidthForText(textEl);
        const availableWidth = Math.max(
            0,
            cellWidth - TREE_MAP_LABEL_PADDING_PX * 2,
        );

        while (textEl.firstChild) textEl.removeChild(textEl.firstChild);

        const nameSpan = document.createElementNS(svgNs, "tspan");
        nameSpan.setAttribute("x", x);
        nameSpan.setAttribute("dy", TREE_MAP_NAME_DY);
        nameSpan.setAttribute("fill", fontColor);
        nameSpan.setAttribute("font-weight", "700");
        nameSpan.textContent = label;
        textEl.appendChild(nameSpan);

        // Truncate to fit. We measure the tspan after attaching, then trim
        // characters from the end until it fits, appending an ellipsis. If
        // even the ellipsis alone is too wide, we let SVG clip naturally.
        if (Number.isFinite(availableWidth) && availableWidth > 0) {
            let measured = nameSpan.getComputedTextLength();
            if (measured > availableWidth) {
                let truncated = label;
                while (truncated.length > 1 && measured > availableWidth) {
                    truncated = truncated.slice(0, -1);
                    nameSpan.textContent = truncated + TREE_MAP_ELLIPSIS;
                    measured = nameSpan.getComputedTextLength();
                }
            }
        }

        // Always render the percentage. If we don't have one (defensive),
        // show "0%" rather than skip; positive sub-one-percent values are
        // rendered as "<1%" by formatTreeMapPercent.
        const pctSpan = document.createElementNS(svgNs, "tspan");
        pctSpan.setAttribute("x", x);
        pctSpan.setAttribute("dy", TREE_MAP_PERCENT_DY);
        pctSpan.setAttribute("fill", fontColor);
        pctSpan.setAttribute("font-weight", "400");
        pctSpan.textContent = expectedPct;
        textEl.appendChild(pctSpan);
    });
}

const AcxTreeMap: React.FC<AcxTreeMapProps> = (props) => {
    const handlesSelection = Boolean(props.onSelectNode);

    // Google TreeMap couples its `select` event to native drilldown, so
    // disabling drilldown kills the click event too. Instead of routing
    // through Google's events API (which is fragile on multi-chart pages —
    // `getChart()` can return null when `ready` fires on the second of two
    // wrappers, leaving its mouse listeners unwired), we identify clicked
    // cells purely from the DOM: the styling pass annotates each cell's
    // `<g>` with `data-row`, and the click handler reads that attribute
    // from the closest ancestor of the click target.
    const propsRef = React.useRef(props);
    React.useEffect(() => {
        propsRef.current = props;
    }, [props]);

    // The chart wrapper is the handle we need to run the styling pass against.
    // Stored in a ref so the MutationObserver (set up in a useEffect below,
    // independent of `ready`) can call the styling pass whenever Google
    // mutates the chart subtree — including the first paint, in case
    // `ready` fires before the SVG is fully laid out.
    const wrapperRef = React.useRef<GoogleChartWrapper | null>(null);
    // Ref'd onto the inner Box that wraps the Chart component, so the
    // observer can watch the chart's subtree by element reference (not by
    // ID lookup against the document, which races with mount timing).
    const chartContainerRef = React.useRef<HTMLDivElement | null>(null);

    const fontColor = theme.palette.white.main;

    const runStylingPass = React.useCallback(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        // Idempotent — re-runs cost nothing if the SVG already matches the
        // desired layout. That's what lets us call it freely from the
        // MutationObserver, from `ready`, and from multiple deferred
        // safety-net schedules without an explicit suppression flag.
        applyTreeMapStyling(wrapper, fontColor, propsRef.current.levelBaseColor);
    }, [fontColor]);

    // MutationObserver lives for the component's lifetime, watching the
    // chart's container element. Doing it here (instead of inside `ready`)
    // means we don't depend on `ready` firing at the right moment for the
    // safety net to engage — late SVG mutations are caught even if `ready`
    // already fired against an incomplete DOM.
    React.useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;
        const observer = new MutationObserver(() => {
            runStylingPass();
        });
        observer.observe(container, {
            childList: true,
            subtree: true,
        });
        return () => observer.disconnect();
    }, [runStylingPass]);

    const chartEvents = React.useMemo(() => {
        return [
            {
                eventName: "ready" as GoogleVizEventName,
                callback: ({
                    chartWrapper,
                }: {
                    chartWrapper: GoogleChartWrapper;
                    google: GoogleViz;
                }) => {
                    wrapperRef.current = chartWrapper;

                    // Round corners, inset cells, and replace flat labels
                    // with bold-name + percent two-line tspans. Idempotent.
                    runStylingPass();

                    // Safety-net passes: Google's `ready` sometimes fires
                    // before its own layout pass completes (intermittent
                    // when multiple charts mount together, when switching
                    // dashboards, etc.). We re-run on the next animation
                    // frame and twice more on small delays so a late-arriving
                    // SVG always picks up our styling. The MutationObserver
                    // is the steady-state safety net for hover redraws;
                    // these are the first-paint safety net.
                    requestAnimationFrame(() => runStylingPass());
                    window.setTimeout(() => runStylingPass(), 50);
                    window.setTimeout(() => runStylingPass(), 200);

                },
            },
        ];
    }, [runStylingPass]);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const current = propsRef.current;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const dataTable = wrapper.getDataTable() as
            | {
                  getValue(row: number, col: number): unknown;
              }
            | null;
        if (!dataTable) return;

        // Walk up from the click target to the nearest `<g>` cell carrying
        // a `data-row` attribute (set by the styling pass). Cells whose
        // styling pass hasn't yet run won't have the attribute, but every
        // chart that's been styled (which is required to be useful anyway)
        // has them.
        const target = e.target as Element | null;
        const rowEl = target?.closest?.("[data-row]") ?? null;
        if (!rowEl) return;
        const row = Number(rowEl.getAttribute("data-row"));
        if (!Number.isFinite(row)) return;

        const id = String(dataTable.getValue(row, 0) ?? "");
        const parentRaw = dataTable.getValue(row, 1);
        const parent =
            parentRaw == null || parentRaw === "" ? null : String(parentRaw);
        const value = Number(dataTable.getValue(row, 2) ?? 0);
        const colorValue = Number(dataTable.getValue(row, 3) ?? 0);
        current.onSelectNode?.({
            id,
            parent,
            value,
            colorValue,
            hasChildren: current.nodesWithChildren.has(id),
        });
    };

    const { midColor } = React.useMemo(
        () => deriveShadeRange(props.levelBaseColor),
        [props.levelBaseColor],
    );

    return (
        <Box
            sx={{
                width: props.width ?? "100%",
                height: props.height ?? "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
            }}
        >
            {props.headerLabel && (
                <Box
                    sx={{
                        flexShrink: 0,
                        backgroundColor: theme.palette.primary[800],
                        color: fontColor,
                        borderRadius: "8px",
                        py: "10px",
                        px: "16px",
                        mb: "6px",
                        mx: "6px",
                        textAlign: "center",
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "12px",
                        fontWeight: theme.typography.fontWeightSemibold,
                        lineHeight: "16px",
                        pointerEvents: "none",
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: "13px",
                            fontWeight:
                                theme.typography.fontWeightSemibold,
                            color: fontColor,
                        }}
                    >
                        {props.headerLabel}
                    </Typography>
                </Box>
            )}
            <Box
                ref={chartContainerRef}
                onClick={handlesSelection ? handleClick : undefined}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    // Hover: darken the cell only. Font-size shifts would
                    // clobber the bold-name + percent two-tspan layout the
                    // styling pass wrote.
                    "& svg > g rect": {
                        transition: "filter 120ms ease",
                    },
                    "& svg > g:hover rect": {
                        filter: "brightness(0.9)",
                    },
                }}
            >
                <Chart
                    width={"100%"}
                    height={"100%"}
                    chartType="TreeMap"
                    loader={<div>Loading Tree Map</div>}
                    data={props.data}
                    options={{
                        // Keep Google's built-in scale neutral. The SVG
                        // styling pass applies the volume-based shades after
                        // layout, which preserves the darker-higher-volume
                        // behavior without making chart initialization depend
                        // on Google's color interpolation.
                        minColor: midColor,
                        midColor,
                        maxColor: midColor,
                        // Suppress Google's built-in header bar — we render our
                        // own above the chart, sourced from headerTemplates.
                        headerHeight: 0,
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 12,
                        fontColor,
                        // One level visible at a time; per-level fetching means
                        // descending is handled by the host re-fetching, not by
                        // Google's built-in drilldown.
                        maxDepth: 1,
                        maxPostDepth: 0,
                        showScale: false,
                        // No tooltips for v1 — Google's built-in tooltip is
                        // disabled (it'd show a small cell-name label that
                        // doesn't match the rest of the Signals chart UX)
                        // and we don't render our own.
                        showTooltips: false,
                        ...(handlesSelection
                            ? {
                                  // Disable native drilldown / rollup (we
                                  // drive zoom via the point menu) and
                                  // highlight / unhighlight (so hover
                                  // doesn't redraw the cell text and clobber
                                  // the bold + percent tspan layout the
                                  // styling pass writes). The underlying
                                  // onmouseover/onmouseout events still fire
                                  // and drive the point-menu selection.
                                  eventsConfig: {
                                      drilldown: [],
                                      rollup: [],
                                      highlight: [],
                                      unhighlight: [],
                                  },
                              }
                            : {}),
                    }}
                    getChartWrapper={(wrapper, google) => {
                        // Capture the wrapper as early as possible so the
                        // observer's first callback (if it fires before
                        // `ready`) can run a styling pass against it.
                        wrapperRef.current = wrapper;
                        props.onChartReady?.(wrapper, google);
                    }}
                    chartEvents={chartEvents}
                    rootProps={{ "data-testid": "AcxTreeMap" }}
                />
            </Box>
        </Box>
    );
};

export default AcxTreeMap;
